import { useEffect, useMemo, useState } from 'react';

import { Box, Button, FormControl, FormLabel, SimpleGrid, Stack } from '@chakra-ui/react';
import { t } from 'i18next';
import { useNavigate, useParams } from 'react-router-dom';

import Page from '../../components/Page';
import useArtistQuery from '../../components/api/queries/useArtistQuery';
import useUserQuery from '../../components/api/queries/useUserQuery';
import DiaryBookCover from './components/DiaryBookCover';
import { ENABLE_DIARY_COVER_CUSTOMIZATION } from '../../constants/photoCardDiary';
import { getArtistId } from '../../utils/etcUtils';
import {
  DIARY_COVER_PRESETS,
  getDiaryCover,
  loadDiaryCoverMap,
  saveDiaryCoverMap
} from '../../utils/photoCardDiaryCoverStorage';

const EDIT_PREVIEW_WIDTH = '208px';
const EDIT_PREVIEW_HEIGHT = '290px';

export default function MyPhotoCardCoverEditPage() {
  const navigate = useNavigate();
  const { user_id: userId, member_id: memberId } = useParams();
  const artistId = getArtistId();
  const artist = useArtistQuery(artistId);
  const user = useUserQuery();
  const boardPath = `/users/${userId}/binder`;
  const artistName = artist?.shortName || artist?.name;
  const collectorName =
    user?.userId === userId
      ? user?.baseUserProfile?.nickname || user?.nickname || undefined
      : undefined;
  const likedMembers = useMemo(() => {
    const members = artist?.members || [];
    const filteredMembers = members.filter((member) => member.isLiked);
    return filteredMembers.length > 0 ? filteredMembers : members;
  }, [artist]);
  const fallbackMembers = useMemo(() => {
    return artist?.members || [];
  }, [artist]);
  const memberIndex = useMemo(() => {
    const likedMemberIndex = likedMembers.findIndex((member) => member.memberId === memberId);
    if (likedMemberIndex !== -1) return likedMemberIndex;
    return fallbackMembers.findIndex((member) => member.memberId === memberId);
  }, [fallbackMembers, likedMembers, memberId]);
  const member = useMemo(() => {
    return (
      likedMembers.find((likedMember) => likedMember.memberId === memberId) ||
      fallbackMembers.find((fallbackMember) => fallbackMember.memberId === memberId)
    );
  }, [fallbackMembers, likedMembers, memberId]);

  const [coverMap, setCoverMap] = useState({});
  const [draftCover, setDraftCover] = useState(null);

  useEffect(() => {
    if (ENABLE_DIARY_COVER_CUSTOMIZATION) return;
    navigate(boardPath, { replace: true });
  }, [boardPath, navigate]);

  useEffect(() => {
    if (!userId || !artistId) return;
    setCoverMap(loadDiaryCoverMap({ userId, artistId }));
  }, [artistId, userId]);

  useEffect(() => {
    if (!artist || member) return;
    navigate(boardPath, { replace: true });
  }, [artist, boardPath, member, navigate]);

  useEffect(() => {
    if (!member) return;
    setDraftCover(
      getDiaryCover({
        coverMap,
        member,
        memberIndex: memberIndex >= 0 ? memberIndex : 0
      })
    );
  }, [coverMap, member, memberIndex]);

  const goToBoard = () => {
    navigate(boardPath);
  };

  const handleSave = () => {
    if (!member || !draftCover) return;

    const nextCoverMap = {
      ...coverMap,
      [member.memberId]: {
        colorId: draftCover.colorId,
        title: draftCover.title.trim() || member.name
      }
    };

    setCoverMap(nextCoverMap);
    saveDiaryCoverMap({ userId, artistId, coverMap: nextCoverMap });
    navigate(boardPath);
  };

  if (!ENABLE_DIARY_COVER_CUSTOMIZATION) return null;

  return (
    <Page
      title={t('myPhotoCardDiary.editCoverTitle', { defaultValue: '표지 꾸미기' })}
      showTitle={true}
      showBackButton={true}
      onHistoryBackClick={goToBoard}
    >
      <Stack spacing={'24px'} paddingTop={'12px'} paddingBottom={'24px'}>
        <Box padding={'0'}>
          <Box
            width={EDIT_PREVIEW_WIDTH}
            maxWidth={'100%'}
            height={EDIT_PREVIEW_HEIGHT}
            marginX={'auto'}
          >
            {draftCover && member
              ? <DiaryBookCover
                  cover={draftCover}
                  memberName={member.name}
                  subtitle={artistName || undefined}
                  badgeText={collectorName}
                />
              : null}
          </Box>
        </Box>

        <Box padding={'0'}>
          <Stack spacing={'20px'}>
            <FormControl>
              <FormLabel fontSize={'14px'} fontWeight={600}>
                {t('myPhotoCardDiary.coverColorLabel', { defaultValue: '표지 컬러' })}
              </FormLabel>
              <SimpleGrid columns={6} spacing={'10px'}>
                {DIARY_COVER_PRESETS.map((preset) => {
                  const isSelected = preset.id === draftCover?.colorId;

                  return (
                    <Box
                      key={preset.id}
                      as="button"
                      type="button"
                      width={'100%'}
                      aspectRatio={1}
                      borderRadius={'18px'}
                      borderWidth={isSelected ? '3px' : '1px'}
                      borderColor={isSelected ? preset.textColor : 'rgba(0,0,0,0.08)'}
                      backgroundColor={preset.coverColor}
                      boxShadow={isSelected ? '0 0 0 2px rgba(0,0,0,0.12)' : 'none'}
                      onClick={() =>
                        setDraftCover((previousDraftCover) => ({
                          ...(previousDraftCover || {}),
                          colorId: preset.id
                        }))
                      }
                    />
                  );
                })}
              </SimpleGrid>
            </FormControl>
          </Stack>
        </Box>

        <Stack direction={'row'} spacing={'10px'}>
          <Button flex={1} variant="ghost" borderRadius={'999px'} onClick={goToBoard}>
            {t('cancel', { defaultValue: '취소' })}
          </Button>
          <Button
            flex={1}
            colorScheme="primary"
            borderRadius={'999px'}
            onClick={handleSave}
            isDisabled={!member || !draftCover}
          >
            {t('save', { defaultValue: '저장' })}
          </Button>
        </Stack>
      </Stack>
    </Page>
  );
}
